import { useMemo } from "react";

export default function useCounselingDerivedData({
  targetList,
  searchQuery,
  filterType,
  selectedDepartment,
  targetGroup,
  sortType,
  visitLogs,
  selectedStudentId,
  students,
  matchSearch,
}) {
  // 0. 효율적인 연산을 위해 모든 학생에 대해 최근 심방일과 횟수를 미리 계산해둡니다.
  const studentMetrics = useMemo(() => {
    const metrics = {};
    targetList.forEach(s => {
      const logs = visitLogs.filter(l => l.studentId === s.id);
      const sortedLogs = [...logs].sort((a, b) => new Date(b.date) - new Date(a.date));
      metrics[s.id] = {
        lastVisitDate: sortedLogs.length > 0 ? sortedLogs[0].date : "",
        visitCount: logs.length
      };
    });
    return metrics;
  }, [targetList, visitLogs]);

  const filteredStudents = useMemo(() => {
    let result = [...targetList];

    // 1. 부서 필터링
    if (selectedDepartment && selectedDepartment !== "All") {
      result = result.filter((s) => {
        if (targetGroup === "students") {
          return (s.department || s.grade) === selectedDepartment;
        }
        return s.charge && s.charge.includes(selectedDepartment);
      });
    }

    // 2. 검색어 필터링
    if (searchQuery) {
      result = result.filter((s) =>
        matchSearch(s.name, searchQuery) ||
        (s.school && matchSearch(s.school, searchQuery)) ||
        (s.phone && s.phone.includes(searchQuery))
      );
    }

    // 3. 필터 타입 (최근심방 / 장기결석)
    const todayCutoff = new Date().toISOString().slice(0, 10);

    if (filterType === "recent") {
      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
      const cutoff = twoWeeksAgo.toISOString().slice(0, 10);
      result = result.filter((s) => {
        const lastDate = studentMetrics[s.id]?.lastVisitDate;
        return lastDate && lastDate >= cutoff;
      });
    }

    if (filterType === "long_absent") {
      const fourWeeksAgo = new Date();
      fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
      const cutoff = fourWeeksAgo.toISOString().slice(0, 10);
      result = result.filter((s) => {
        const lastDate = studentMetrics[s.id]?.lastVisitDate;
        return !lastDate || lastDate < cutoff;
      });
    }

    // 4. 정렬
    return result.sort((a, b) => {
      if (sortType === "recent") {
        const lastA = studentMetrics[a.id]?.lastVisitDate;
        const lastB = studentMetrics[b.id]?.lastVisitDate;
        if (!lastA && !lastB) return a.name.localeCompare(b.name);
        if (!lastA) return 1;
        if (!lastB) return -1;
        return lastB.localeCompare(lastA);
      }
      if (sortType === "grade") {
        const getWeight = (s) => {
          const val = (s.grade || s.department || "").trim();
          const match = val.match(/(초|중|고)\s*(\d)/);
          if (match) {
            const base = match[1] === "초" ? 10 : match[1] === "중" ? 20 : 30;
            return base + parseInt(match[2], 10);
          }
          return 999;
        };
        const weightA = getWeight(a);
        const weightB = getWeight(b);
        if (weightA !== weightB) return weightA - weightB;
        return a.name.localeCompare(b.name);
      }
      return a.name.localeCompare(b.name);
    });
  }, [targetList, searchQuery, filterType, selectedDepartment, targetGroup, sortType, studentMetrics, matchSearch]);

  const selectedStudent = useMemo(
    () => targetList.find((s) => s.id === selectedStudentId),
    [targetList, selectedStudentId]
  );

  const studentLogs = useMemo(() => {
    if (!selectedStudentId) return [];
    return visitLogs
      .filter((log) => log.studentId === selectedStudentId)
      .sort((a, b) => {
        const dateDiff = new Date(b.date) - new Date(a.date);
        if (dateDiff !== 0) return dateDiff;
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      });
  }, [visitLogs, selectedStudentId]);

  const thisMonthLogs = useMemo(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth() + 1;
    return visitLogs.filter((l) => {
      const d = new Date(l.date);
      return d.getFullYear() === y && d.getMonth() + 1 === m;
    });
  }, [visitLogs]);

  const longAbsentStudents = useMemo(() => {
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    const cutoff = threeMonthsAgo.toISOString().slice(0, 10);

    return students.filter((s) => {
      const lastDate = studentMetrics[s.id]?.lastVisitDate;
      return !lastDate || lastDate < cutoff;
    });
  }, [students, studentMetrics]);

  return {
    filteredStudents,
    selectedStudent,
    studentLogs,
    thisMonthLogs,
    longAbsentStudents,
    studentMetrics,
  };
}
